import React from 'react';
import { Leaf, Sparkles, Clock, Flame, Drumstick, Fish } from 'lucide-react';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from './ui/carousel';

const Menu = ({ pastMenu, nextMenu, lastPastEvent, nextEvent, loading, pastMenusByEvent = [], showDrinks = true }) => {
  if (loading) {
    return (
      <section id="menu" className="px-4 py-24 bg-black sm:px-6 md:py-32">
        <div className="mx-auto max-w-5xl text-center">
          <p className="text-gray-500">Chargement du menu...</p>
        </div>
      </section>
    );
  }

  // Helper function to render a dish
  const renderDish = (dish, idx) => {
    const flagsCount = [dish.vegetarian, dish.meat, dish.spicy, dish.fish].filter(Boolean).length;
    const rows = Math.ceil(flagsCount / 2);
    return (
    <div key={idx} className="pb-4 border-b group border-white/10 last:border-b-0">
      <div className={`relative mb-2 ${rows > 1 ? 'pb-6' : ''}`}>
        <h5 className="pr-20 font-semibold text-gray-300 text-md">{dish.name}</h5>
        {(dish.vegetarian || dish.meat || dish.spicy || dish.fish) && (
          <div className="grid absolute top-0 right-0 grid-cols-2 gap-1">
            {dish.vegetarian ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-900/30 text-green-500 text-sm rounded">
                <Leaf size={14} />
              </span>
            ) : null}
            {dish.meat ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-900/30 text-red-400 text-sm rounded">
                <Drumstick size={14} />
              </span>
            ) : null}
            {dish.spicy ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-900/30 text-orange-400 text-sm rounded">
                <Flame size={14} />
              </span>
            ) : null}
            {dish.fish ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-900/30 text-blue-400 text-sm rounded">
                <Fish size={14} />
              </span>
            ) : null}
          </div>
        )}
      </div>
      {dish.ingredients && dish.ingredients.length > 0 && (
        <ul className="space-y-0.5">
          {dish.ingredients.map((ingredient, i) => (
            <li key={i} className="text-sm text-gray-500">
              {ingredient}
            </li>
          ))}
        </ul>
      )}
      {dish.description && (
        <p className="mt-1 text-sm italic text-gray-500">
          {dish.description}
        </p>
      )}
    </div>
  )};

  const pastSlides = Array.isArray(pastMenusByEvent)
    ? [...pastMenusByEvent].sort((a, b) => a.event.volume - b.event.volume)
    : [];
  let slides = [...pastSlides.map(p => ({ ...p, isCurrent: false }))];
  if (nextEvent) {
    slides = [...slides, { event: nextEvent, menu: nextMenu, isCurrent: true }];
  }
  slides.sort((a, b) => a.event.volume - b.event.volume);
  const currentIndex = (() => {
    const activeSlideIndex = slides.findIndex((slide) => slide.isCurrent);
    return activeSlideIndex >= 0 ? activeSlideIndex : Math.max(0, slides.length - 1);
  })();
  const totalSlides = slides.length;
  const sortedPast = Array.isArray(pastMenusByEvent) ? [...pastMenusByEvent].sort((a, b) => a.event.volume - b.event.volume) : [];
  const [carouselApi, setCarouselApi] = React.useState(null);
  const [selectedIndex, setSelectedIndex] = React.useState(currentIndex);

  React.useEffect(() => {
    setSelectedIndex(currentIndex);
    if (carouselApi && carouselApi.selectedScrollSnap() !== currentIndex) {
      carouselApi.scrollTo(currentIndex, true);
    }
  }, [carouselApi, currentIndex]);

  React.useEffect(() => {
    if (!carouselApi) return;
    const onSelect = () => setSelectedIndex(carouselApi.selectedScrollSnap());
    onSelect();
    carouselApi.on('select', onSelect);
    carouselApi.on('reInit', onSelect);
    return () => {
      try { carouselApi.off('select', onSelect); } catch {}
      try { carouselApi.off('reInit', onSelect); } catch {}
    };
  }, [carouselApi]);

  const renderMenuCard = (menu, eventTitle) => {
    const starters = menu?.dishes?.filter(d => d.type === 'starter') || [];
    const mains = menu?.dishes?.filter(d => d.type === 'main') || [];
    const desserts = menu?.dishes?.filter(d => d.type === 'dessert') || [];
    const drinks = menu?.dishes?.filter(d => d.type === 'drink') || [];
    return (
      <div className="relative p-4 w-full rounded-lg sm:p-8">
        <div className="pointer-events-none absolute -inset-px rounded-[inherit]" />
        {eventTitle && (
          <div className="flex gap-2 items-center mb-8">
            <Sparkles size={16} className="text-white" />
            <h3 className="text-sm uppercase tracking-[0.3em] text-white font-semibold">
              {eventTitle}
            </h3>
          </div>
        )}
        <div className="grid grid-cols-1 gap-6 md:gap-8 md:grid-cols-2 lg:grid-cols-3">
          {starters.length > 0 && (
            <div className="relative overflow-hidden rounded-xl border border-white/10 bg-gray-900/30 shadow-[0_0_0_1px_rgba(255,255,255,0.06)_inset,0_8px_24px_rgba(0,0,0,0.4)]">
              <div className="pointer-events-none absolute -inset-px rounded-[inherit] bg-gradient-to-br from-white/5 via-transparent to-transparent opacity-20" />
              <div className="flex justify-between items-center px-4 py-3 border-b border-white/10">
                <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gray-300">
                  Entrées
                </div>
                <span className="px-2 py-0.5 text-[10px] rounded bg-white/5 text-gray-400">{starters.length}</span>
              </div>
              <div className="p-4 space-y-4">
                {starters.map((dish, i) => renderDish(dish, i))}
              </div>
            </div>
          )}
          {mains.length > 0 && (
            <div className="relative overflow-hidden rounded-xl border border-white/10 bg-gray-900/30 shadow-[0_0_0_1px_rgba(255,255,255,0.06)_inset,0_8px_24px_rgba(0,0,0,0.4)]">
              <div className="pointer-events-none absolute -inset-px rounded-[inherit] bg-gradient-to-br from-white/5 via-transparent to-transparent opacity-20" />
              <div className="flex justify-between items-center px-4 py-3 border-b border-white/10">
                <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gray-300">
                  Plats
                </div>
                <span className="px-2 py-0.5 text-[10px] rounded bg-white/5 text-gray-400">{mains.length}</span>
              </div>
              <div className="p-4 space-y-4">
                {mains.map((dish, i) => renderDish(dish, i))}
              </div>
            </div>
          )}
          {desserts.length > 0 && (
            <div className="relative overflow-hidden rounded-xl border border-white/10 bg-gray-900/30 shadow-[0_0_0_1px_rgba(255,255,255,0.06)_inset,0_8px_24px_rgba(0,0,0,0.4)]">
              <div className="pointer-events-none absolute -inset-px rounded-[inherit] bg-gradient-to-br from-white/5 via-transparent to-transparent opacity-20" />
              <div className="flex justify-between items-center px-4 py-3 border-b border-white/10">
                <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gray-300">
                  Dessert
                </div>
                <span className="px-2 py-0.5 text-[10px] rounded bg-white/5 text-gray-400">{desserts.length}</span>
              </div>
              <div className="p-4 space-y-4">
                {desserts.map((dish, i) => renderDish(dish, i))}
              </div>
            </div>
          )}
          {showDrinks && drinks.length > 0 && (
            <div className="relative overflow-hidden rounded-xl border border-white/10 bg-gray-900/30 shadow-[0_0_0_1px_rgba(255,255,255,0.06)_inset,0_8px_24px_rgba(0,0,0,0.4)]">
              <div className="pointer-events-none absolute -inset-px rounded-[inherit] bg-gradient-to-br from-white/5 via-transparent to-transparent opacity-20" />
              <div className="flex justify-between items-center px-4 py-3 border-b border-white/10">
                <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gray-300">
                  Boissons
                </div>
                <span className="px-2 py-0.5 text-[10px] rounded bg-white/5 text-gray-400">{drinks.length}</span>
              </div>
              <div className="p-4 space-y-4">
                {drinks.map((dish, i) => renderDish(dish, i))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <section id="menu" className="relative px-4 py-24 bg-black bg-gradient-to-b from-black to-gray-950 sm:px-6">
      <div aria-hidden className="absolute left-0 top-1/4 w-64 h-64 rounded-full blur-3xl -translate-x-1/3 pointer-events-none bg-white/5" />
      <div aria-hidden className="absolute right-0 bottom-1/4 w-64 h-64 rounded-full blur-3xl translate-x-1/3 pointer-events-none bg-white/5" />
      <div className="relative z-10 mx-auto max-w-7xl">
        <div className="mb-12 text-center">
          <h2 className="text-4xl font-black tracking-tight md:text-6xl">
            LE MENU
          </h2>
        </div>
        <div className="flex flex-wrap gap-3 justify-center items-center my-6 text-xs text-gray-400">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-green-900/30 text-green-500">
            <Leaf size={14} /> Végétarien
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-red-900/30 text-red-400">
            <Drumstick size={14} /> Viande
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-orange-900/30 text-orange-400">
            <Flame size={14} /> Épicé
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-900/30 text-blue-400">
            <Fish size={14} /> Poisson
          </span>
        </div>
        <Carousel className="relative" setApi={setCarouselApi} opts={{ startIndex: currentIndex }}>
          <CarouselContent>
            {slides.map(({ event, menu, isCurrent }, idx) => (
              <CarouselItem key={`${isCurrent ? 'current' : event.id}-${idx}`}>
                {isCurrent ? (
                  nextMenu && nextMenu.dishes && nextMenu.dishes.length > 0 ? (
                    renderMenuCard(nextMenu, nextEvent ? `Vol.${nextEvent.volume} — ${nextEvent.theme}` : 'Prochainement')
                  ) : (
                    <div className="relative bg-gradient-to-br from-gray-900 to-gray-950 rounded-lg p-8 md:p-12 overflow-hidden min-h-[360px] flex flex-col justify-center items-center text-center border border-white/10 shadow-[0_0_0_1px_rgba(255,255,255,0.10)_inset,0_0_28px_rgba(255,255,255,0.06)]">
                      <div className="absolute top-0 right-0 w-36 h-36 rounded-full blur-3xl bg-white/5" />
                      <div className="absolute bottom-0 left-0 w-28 h-28 rounded-full blur-3xl bg-white/5" />
                      <div className="relative z-10">
                        <div className="flex justify-center items-center mx-auto mb-6 w-16 h-16 rounded-full bg-white/10">
                          <Clock size={32} className="text-white/60" />
                        </div>
                        <h4 className="mb-4 text-2xl font-bold md:text-3xl">
                          Nouveau menu en préparation
                        </h4>
                        <p className="mx-auto mb-6 max-w-xs text-gray-400">
                          Un nouveau thème, de nouvelles saveurs... Restez connectés pour découvrir le menu {nextEvent ? `du Vol.${nextEvent.volume}` : ''}
                        </p>
                        <div className="inline-flex gap-2 items-center px-4 py-2 text-sm text-gray-300 rounded-full bg-white/10">
                          Révélation bientôt
                        </div>
                      </div>
                    </div>
                  )
                ) : (
                  renderMenuCard(menu, `Vol.${event.volume} — ${event.theme}`)
                )}
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="hidden md:flex" />
          <CarouselNext className="hidden md:flex" />
        </Carousel>
        <div className="flex flex-wrap gap-2 justify-center items-center mt-2">
          {slides.map(({ event }, i) => (
            <button
              key={`${event.id ?? 'current'}-${i}`}
              type="button"
              onClick={() => carouselApi?.scrollTo(i)}
              className={`${selectedIndex === i ? 'bg-white text-black' : 'bg-white/5 text-white hover:bg-white/10'} px-3 py-1 text-xs rounded-full border border-white/15 transition-colors`}
              aria-label={`Aller à la diapositive ${i + 1}`}
              aria-current={selectedIndex === i ? 'true' : 'false'}
            >
              {event?.volume ? `Vol.${event.volume}` : 'À venir'}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Menu;
